import type {
  ApiAuditLog,
  ApiCategory,
  ApiCategoryDetail,
  ApiCategoryListItem,
  ApiMobileAppPlatform,
  ApiMobileAppRelease,
  ApiMobileAppReleaseStatus,
  ApiMobileAppPlatformSummary,
  ApiEmailMessageLog,
  ApiInAppNotification,
  ApiNotificationCatalogEvent,
  ApiNotificationPreference,
  ApiNotificationRoleDefault,
  ApiPermissionCatalogItem,
  ApiPlant,
  ApiPushDevice,
  ApiRole,
  ApiShift,
  ApiUserDetail,
  ApiUserListItem,
} from "@tm-food/api-contract";

export type Plant = {
  id: string;
  code: string;
  name: string;
  status: string;
  timezone: string;
  createdAt: string;
};

export type SysShift = {
  id: string;
  plantCode: string;
  code: string;
  name: string;
  startTime: string;
  endTime: string;
  status: string;
  createdAt: string;
};

export type CreateShiftInput = {
  plantCode: string;
  code: string;
  name: string;
  startTime: string;
  endTime: string;
};

export type SysCategoryListItem = {
  id: string;
  code: string;
  name: string;
  createdAt: string;
  updatedAt: string | null;
};

export type SysCategoryDetail = {
  id: string;
  code: string;
  name: string;
  value: string;
  description: string;
  orderNo: number;
  createdAt: string;
  updatedAt: string | null;
};

export type SysCategory = {
  id: string;
  code: string;
  name: string;
  createdAt: string;
  updatedAt: string | null;
  details: SysCategoryDetail[];
};

export type CreateCategoryInput = {
  code: string;
  name: string;
};

export type UpdateCategoryInput = {
  name: string;
};

export type CreateCategoryDetailInput = {
  code: string;
  name: string;
  value?: string;
  description?: string;
  orderNo?: number;
};

export type UpdateCategoryDetailInput = {
  name: string;
  value?: string;
  description?: string;
  orderNo?: number;
};

export type NotificationRoleDefault = {
  eventType: string;
  roleCodes: string[];
  plantScoped: boolean;
};

export type PermissionCatalogItem = {
  code: string;
  module: string;
  action: string;
  description: string | null;
};

export type CreatePlantInput = {
  code: string;
  name: string;
  timezone?: string;
};

export type UpdatePlantInput = {
  name: string;
  timezone?: string;
};

export type CreateUserInput = {
  username: string;
  fullName: string;
  password: string;
  defaultPlantId: string;
  roleCodes: string[];
  email?: string;
  department?: string;
};

export type UpdateUserInput = {
  fullName: string;
  defaultPlantId: string;
  roleCodes: string[];
  email?: string;
  department?: string;
};

export type CreateRoleInput = {
  code: string;
  name: string;
  scope: "Plant" | "Org";
};

export type UpdateRoleInput = {
  name: string;
  scope: "Plant" | "Org";
  status?: "Active" | "Inactive";
};

export type SysUser = {
  id: string;
  username: string;
  fullName: string;
  status: string;
  defaultPlantId: string | null;
  defaultPlantCode: string | null;
  roleCodes: string[];
  createdAt: string;
};

export type SysUserDetail = SysUser & {
  email: string | null;
  department: string | null;
  failedLoginCount: number;
  lockedAt: string | null;
};

export type SysRole = {
  id: string;
  code: string;
  name: string;
  scope: string;
  permissions: string[];
  status: string;
};

export type AuditLog = {
  id: string;
  actorUserId: string | null;
  actorUsername: string | null;
  entityType: string;
  entityId: string;
  action: string;
  createdAt: string;
  beforeJson: string | null;
  afterJson: string | null;
  ip: string | null;
};

export type EmailMessageLog = {
  id: string;
  to: string;
  subject: string;
  templateCode: string | null;
  status: string;
  error: string | null;
  relatedEntityType: string | null;
  relatedEntityId: string | null;
  createdAt: string;
};

export function auditHasDelta(row: AuditLog): boolean {
  return Boolean(row.beforeJson?.trim() || row.afterJson?.trim());
}

/** Pretty-print stored audit JSON; fall back to raw string if invalid. */
export function formatAuditJson(raw: string | null | undefined): string {
  if (!raw?.trim()) return "—";
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

export type NotificationPreference = {
  eventType: string;
  channel: string;
  enabled: boolean;
};

export type NotificationCatalogEvent = {
  eventType: string;
  groupCode: string;
  groupLabelVi: string;
  nameVi: string;
  description: string | null;
  sortOrder: number;
  isEnabled: boolean;
  updatedAt: string;
};

export type InAppNotification = {
  id: string;
  eventType: string;
  title: string;
  body: string;
  severity: string;
  entityType: string | null;
  entityId: string | null;
  plantCode: string | null;
  routeHint: string | null;
  createdAt: string;
  readAt: string | null;
  isRead: boolean;
};

export type PushDevice = {
  id: string;
  platform: string;
  deviceToken: string;
  channelPref: string;
  status: string;
  lastSeenAt: string;
};

export function mapPlant(dto: ApiPlant): Plant {
  return { ...dto };
}

export function mapShift(dto: ApiShift): SysShift {
  return { ...dto };
}

export function mapCategoryDetail(dto: ApiCategoryDetail): SysCategoryDetail {
  return {
    id: dto.id,
    code: dto.code,
    name: dto.name,
    value: dto.value ?? "",
    description: dto.description ?? "",
    orderNo: dto.orderNo ?? 0,
    createdAt: dto.createdAt ?? "",
    updatedAt: dto.updatedAt ?? null,
  };
}

export function mapCategoryListItem(dto: ApiCategoryListItem): SysCategoryListItem {
  return {
    id: dto.id,
    code: dto.code,
    name: dto.name,
    createdAt: dto.createdAt ?? "",
    updatedAt: dto.updatedAt ?? null,
  };
}

export type MobileAppPlatform = ApiMobileAppPlatform;
export type MobileAppReleaseStatus = ApiMobileAppReleaseStatus;

export type SysMobileAppRelease = {
  id: string;
  platform: MobileAppPlatform;
  versionName: string;
  releaseNotes: string;
  downloadUrl: string;
  forceUpdate: boolean;
  status: MobileAppReleaseStatus;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string | null;
};

export type UpsertMobileAppReleaseInput = {
  platform: MobileAppPlatform;
  versionName: string;
  releaseNotes: string;
  downloadUrl: string;
  forceUpdate: boolean;
};

export function mobileAppPlatformLabel(platform: MobileAppPlatform): string {
  return platform === "Ios" ? "iOS" : "Android";
}

export function mapMobileAppRelease(dto: ApiMobileAppRelease): SysMobileAppRelease {
  return {
    id: dto.id,
    platform: dto.platform,
    versionName: dto.versionName,
    releaseNotes: dto.releaseNotes ?? "",
    downloadUrl: dto.downloadUrl ?? "",
    forceUpdate: Boolean(dto.forceUpdate),
    status: dto.status,
    publishedAt: dto.publishedAt ?? null,
    createdAt: dto.createdAt ?? "",
    updatedAt: dto.updatedAt ?? null,
  };
}

export type SysMobileAppPlatformSummary = {
  platform: MobileAppPlatform;
  published: SysMobileAppRelease | null;
  latest: SysMobileAppRelease | null;
  draft: SysMobileAppRelease | null;
  current: SysMobileAppRelease | null;
  versionCount: number;
};

export function mapMobileAppPlatformSummary(
  dto: ApiMobileAppPlatformSummary,
): SysMobileAppPlatformSummary {
  return {
    platform: dto.platform,
    published: dto.published ? mapMobileAppRelease(dto.published) : null,
    latest: dto.latest ? mapMobileAppRelease(dto.latest) : null,
    draft: dto.draft ? mapMobileAppRelease(dto.draft) : null,
    current: dto.current ? mapMobileAppRelease(dto.current) : null,
    versionCount: dto.versionCount ?? 0,
  };
}

export function mapCategory(dto: ApiCategory): SysCategory {
  return {
    id: dto.id,
    code: dto.code,
    name: dto.name,
    createdAt: dto.createdAt ?? "",
    updatedAt: dto.updatedAt ?? null,
    details: (dto.details ?? []).map(mapCategoryDetail),
  };
}

export function mapRoleDefault(dto: ApiNotificationRoleDefault): NotificationRoleDefault {
  return {
    eventType: dto.eventType,
    roleCodes: [...dto.roleCodes],
    plantScoped: Boolean(dto.plantScoped),
  };
}

export function mapPermission(dto: ApiPermissionCatalogItem): PermissionCatalogItem {
  let module = dto.module;
  let description = dto.description ?? null;

  if (dto.code === "bi.prod.list" || dto.code === "sys.dashboard.prod.list" || dto.code === "sys.dashboard.prod.view") {
    module = "sys";
    description = "Xem tổng quan sản xuất";
  } else if (dto.code === "bi.qc.list" || dto.code === "sys.dashboard.qc.list" || dto.code === "sys.dashboard.qc.view") {
    module = "sys";
    description = "Xem tổng quan chất lượng";
  } else if (dto.code === "bi.wh.list" || dto.code === "sys.dashboard.wh.list" || dto.code === "sys.dashboard.wh.view") {
    module = "sys";
    description = "Xem tổng quan kho";
  }

  return {
    code: dto.code,
    module,
    action: dto.action,
    description,
  };
}

export function mapUser(dto: ApiUserListItem): SysUser {
  return {
    ...dto,
    roleCodes: [...(dto.roleCodes ?? [])],
  };
}

export function mapUserDetail(dto: ApiUserDetail): SysUserDetail {
  return {
    ...mapUser(dto),
    email: dto.email ?? null,
    department: dto.department ?? null,
    failedLoginCount: dto.failedLoginCount ?? 0,
    lockedAt: dto.lockedAt ?? null,
  };
}

export function mapRole(dto: ApiRole): SysRole {
  return {
    ...dto,
    permissions: [...dto.permissions],
    status: dto.status ?? "Active",
  };
}

export function mapAudit(dto: ApiAuditLog): AuditLog {
  return {
    id: dto.id,
    actorUserId: dto.actorUserId ?? null,
    actorUsername: dto.actorUsername ?? null,
    entityType: dto.entityType,
    entityId: dto.entityId,
    action: dto.action,
    createdAt: dto.createdAt,
    beforeJson: dto.beforeJson ?? null,
    afterJson: dto.afterJson ?? null,
    ip: dto.ip ?? null,
  };
}

export function mapEmailLog(dto: ApiEmailMessageLog): EmailMessageLog {
  return {
    id: dto.id,
    to: dto.to,
    subject: dto.subject,
    templateCode: dto.templateCode ?? null,
    status: dto.status,
    error: dto.error ?? null,
    relatedEntityType: dto.relatedEntityType ?? null,
    relatedEntityId: dto.relatedEntityId ?? null,
    createdAt: dto.createdAt,
  };
}

export function mapPreference(dto: ApiNotificationPreference): NotificationPreference {
  return { ...dto };
}

export function mapCatalogEvent(dto: ApiNotificationCatalogEvent): NotificationCatalogEvent {
  return {
    eventType: dto.eventType,
    groupCode: dto.groupCode,
    groupLabelVi: dto.groupLabelVi,
    nameVi: dto.nameVi,
    description: dto.description ?? null,
    sortOrder: dto.sortOrder,
    isEnabled: Boolean(dto.isEnabled),
    updatedAt: dto.updatedAt,
  };
}

export function mapInAppNotification(dto: ApiInAppNotification): InAppNotification {
  return {
    ...dto,
    plantCode: dto.plantCode ?? null,
    routeHint: dto.routeHint ?? null,
  };
}

export function mapPushDevice(dto: ApiPushDevice): PushDevice {
  return { ...dto };
}


export type NotificationEntityDetails = {
  lotCode?: string;
  batchCode?: string;
  orderNo?: string;
  ncrNo?: string;
  holdId?: string;
  prNo?: string;
  poNo?: string;
  soNo?: string;
  demandNo?: string;
  approvalId?: string;
  operationCode?: string;
  operationId?: string;
};

/**
 * Extracts business entity codes (lot, batch, MO, NCR, operation, etc.) from an in-app notification.
 * Handles structured JSON payloads, formatted text strings, and entityId/entityType metadata.
 */
export function extractNotificationEntities(n: InAppNotification): NotificationEntityDetails {
  const result: NotificationEntityDetails = {};

  // 1. Try parsing JSON body if present
  const rawBody = n.body?.trim() ?? "";
  if (rawBody.startsWith("{") && rawBody.endsWith("}")) {
    try {
      const obj = JSON.parse(rawBody) as Record<string, unknown>;
      const read = (...keys: string[]) => {
        for (const k of keys) {
          const v = obj[k];
          if (typeof v === "string" && v.trim()) return v.trim();
        }
        return undefined;
      };
      result.lotCode = read("lotCode", "LotCode");
      result.batchCode = read("batchCode", "BatchCode");
      result.orderNo = read("orderNo", "OrderNo", "moNo", "MoNo");
      result.ncrNo = read("ncrNo", "NcrNo", "reason", "Reason");
      result.holdId = read("holdId", "HoldId");
      result.prNo = read("prNo", "PrNo", "requisitionNo", "RequisitionNo");
      result.poNo = read("poNo", "PoNo");
      result.soNo = read("soNo", "SoNo");
      result.demandNo = read("demandNo", "DemandNo");
      result.approvalId = read("approvalId", "ApprovalId", "instanceId", "InstanceId");
      result.operationCode = read("operationCode", "OperationCode");
      result.operationId = read("opId", "OpId");
    } catch {
      // not json
    }
  }

  // Helper to distinguish business codes (e.g. MO-123, B-1, LOT-01) from normal words
  const isCodeLike = (s?: string) => Boolean(s && /^(?:[A-Z0-9]+[-_]|[-_A-Z0-9]*\d)[A-Z0-9\-_]*$/i.test(s));

  // 2. Parse from text body / title (search body first for specifics, then title)
  const text = `${n.title ?? ""} ${rawBody}`.trim();
  const sources = [rawBody, text];
  for (const src of sources) {
    if (!src) continue;
    if (!result.lotCode) {
      const matches = src.matchAll(/(?:Lô|Lot)[:\s]+([A-Za-z0-9\-_]+)/gi);
      for (const m of matches) {
        if (m[1] && isCodeLike(m[1])) {
          result.lotCode = m[1].trim();
          break;
        }
      }
    }
    if (!result.batchCode) {
      const matches = src.matchAll(/(?:Batch|Lô\s+SX)[:\s]+([A-Za-z0-9\-_]+)/gi);
      for (const m of matches) {
        if (m[1] && isCodeLike(m[1])) {
          result.batchCode = m[1].trim();
          break;
        }
      }
    }
    if (!result.orderNo) {
      const matches = src.matchAll(/(?:Lệnh(?:\s+sản\s+xuất|\s+SX)?|MO)[:\s]+([A-Za-z0-9\-_]+)/gi);
      for (const m of matches) {
        if (m[1] && isCodeLike(m[1])) {
          result.orderNo = m[1].trim();
          break;
        }
      }
    }
    if (!result.ncrNo) {
      const m = src.match(/(NCR-[A-Za-z0-9\-_]+)/i);
      if (m?.[1]) result.ncrNo = m[1].trim();
    }
    if (!result.holdId) {
      const m = src.match(/(HOLD-[A-Za-z0-9\-_]+)/i);
      if (m?.[1]) result.holdId = m[1].trim();
    }
    if (!result.prNo) {
      const m = src.match(/(PR-[A-Za-z0-9\-_]+)/i);
      if (m?.[1]) result.prNo = m[1].trim();
    }
    if (!result.poNo) {
      const m = src.match(/(PO-[A-Za-z0-9\-_]+)/i);
      if (m?.[1]) result.poNo = m[1].trim();
    }
    if (!result.soNo) {
      const m = src.match(/(SO-[A-Za-z0-9\-_]+)/i);
      if (m?.[1]) result.soNo = m[1].trim();
    }
    if (!result.demandNo) {
      const m = src.match(/(DMD-[A-Za-z0-9\-_]+)/i);
      if (m?.[1]) result.demandNo = m[1].trim();
    }
    if (!result.operationCode) {
      const m = src.match(/(?:Mã|Công\s+đoạn)[:\s]+([A-Za-z0-9\-_]+)/i);
      if (m?.[1] && isCodeLike(m[1])) result.operationCode = m[1].trim();
    }
  }

  // 3. Fallback from entityId / entityType
  const eid = n.entityId?.trim();
  const etype = (n.entityType ?? "").toLowerCase();
  if (eid) {
    if (etype === "hold" || etype === "meshold" || eid.startsWith("HOLD-")) {
      if (!result.holdId) result.holdId = eid;
    } else if (etype === "ncr" || eid.startsWith("NCR-")) {
      if (!result.ncrNo) result.ncrNo = eid;
    } else if (etype === "pr" || etype === "purchaserequisition" || eid.startsWith("PR-")) {
      if (!result.prNo) result.prNo = eid;
    } else if (etype === "po" || etype === "purchaseorder" || eid.startsWith("PO-")) {
      if (!result.poNo) result.poNo = eid;
    } else if (etype === "so" || etype === "salesorder" || eid.startsWith("SO-")) {
      if (!result.soNo) result.soNo = eid;
    } else if (etype === "demand" || eid.startsWith("DMD-")) {
      if (!result.demandNo) result.demandNo = eid;
    } else if (etype === "batch" || etype === "mesbatch" || eid.startsWith("B-")) {
      if (!result.batchCode) result.batchCode = eid;
    } else if (etype === "productionorder" || etype === "mo" || (n.eventType === "ProductionDelay" && eid.startsWith("MO-"))) {
      if (!result.orderNo) result.orderNo = eid;
    } else if (
      etype === "lot" ||
      eid.startsWith("LOT-") ||
      eid.startsWith("FG-") ||
      eid.startsWith("RM-") ||
      eid.startsWith("WIP-") ||
      (!result.lotCode && (n.eventType === "LotHeld" || n.eventType === "ExpiryWarning"))
    ) {
      if (!result.lotCode) result.lotCode = eid;
    } else if (etype === "operation" && !result.operationId) {
      result.operationId = eid;
    } else if ((etype === "approval" || etype === "approvalinstance") && !result.approvalId) {
      result.approvalId = eid;
    }
  }

  return result;
}

/** Deep-link path for inbox tap (N2). Resolves to specific entity detail views. */
export function resolveNotificationRoute(
  n: InAppNotification,
  shell: "field" | "exe" | "ops" = "field",
): string | null {
  const entities = extractNotificationEntities(n);

  if (shell === "ops") {
    // 1. If explicit specific deep link in routeHint was provided (e.g. /ops/xxx/123)
    if (n.routeHint?.startsWith("/ops/") && n.routeHint.split("/").length > 3) {
      return n.routeHint.trim();
    }

    // 2. Specific entity detail views in OPS shell
    // Production delay event points directly to the Production Order (MO) detail
    if (n.eventType === "ProductionDelay" && entities.orderNo) {
      return `/ops/pln/orders/${encodeURIComponent(entities.orderNo)}`;
    }
    if (entities.batchCode) {
      return `/ops/mes/batches/${encodeURIComponent(entities.batchCode)}`;
    }
    if (entities.orderNo) {
      return `/ops/pln/orders/${encodeURIComponent(entities.orderNo)}`;
    }
    if (entities.lotCode) {
      return `/ops/trc/lots/${encodeURIComponent(entities.lotCode)}`;
    }
    if (entities.ncrNo) {
      return `/ops/qms/ncrs/${encodeURIComponent(entities.ncrNo)}`;
    }
    if (entities.holdId) {
      return `/ops/mes/holds/${encodeURIComponent(entities.holdId)}`;
    }
    if (entities.prNo) {
      return `/ops/pur/requisitions/${encodeURIComponent(entities.prNo)}`;
    }
    if (entities.poNo) {
      return `/ops/pur/orders/${encodeURIComponent(entities.poNo)}`;
    }
    if (entities.soNo) {
      return `/ops/sal/orders/${encodeURIComponent(entities.soNo)}`;
    }
    if (entities.demandNo) {
      return `/ops/pln/demands/${encodeURIComponent(entities.demandNo)}`;
    }

    // 3. Entity type fallback with entityId
    if (n.entityType && n.entityId) {
      const type = n.entityType.toLowerCase();
      if (type === "lot") return `/ops/trc/lots/${encodeURIComponent(n.entityId)}`;
      if (type === "batch" || type === "mesbatch") return `/ops/mes/batches/${encodeURIComponent(n.entityId)}`;
      if (type === "mo" || type === "productionorder") return `/ops/pln/orders/${encodeURIComponent(n.entityId)}`;
      if (type === "meshold" || type === "hold") return `/ops/mes/holds/${encodeURIComponent(n.entityId)}`;
      if (type === "ncr") return `/ops/qms/ncrs/${encodeURIComponent(n.entityId)}`;
      if (type === "pr" || type === "purchaserequisition") return `/ops/pur/requisitions/${encodeURIComponent(n.entityId)}`;
      if (type === "po" || type === "purchaseorder") return `/ops/pur/orders/${encodeURIComponent(n.entityId)}`;
      if (type === "so" || type === "salesorder") return `/ops/sal/orders/${encodeURIComponent(n.entityId)}`;
      if (type === "demand") return `/ops/pln/demands/${encodeURIComponent(n.entityId)}`;
      if (type === "approval") return `/ops/sys/approvals?q=${encodeURIComponent(n.entityId)}`;
    }

    // 4. Route hint
    if (n.routeHint?.startsWith("/ops/")) {
      return n.routeHint.trim();
    }
    if (n.routeHint?.startsWith("/")) {
      return n.routeHint.replace(/^\/(?:mobile|field|exe|executive|app)/, "/ops");
    }

    // 5. Specific event views in OPS shell (when no specific entity is present)
    switch (n.eventType) {
      case "ApprovalPending":
        return entities.approvalId
          ? `/ops/sys/approvals?q=${encodeURIComponent(entities.approvalId)}`
          : "/ops/sys/approvals";
      case "LotHeld":
        return "/ops/mes/holds";
      case "QcPending":
        return "/ops/qms/plans";
      case "ProductionDelay":
        return "/ops/pln/orders";
      case "BatchCompleted":
      case "OperationStarted":
      case "OperationReady":
        return "/ops/mes/batches";
      case "ExpiryWarning":
        return "/ops/wms/expiry";
      case "CipRequired":
      case "CipOverdue":
        return "/ops/mes/batches";
      default:
        break;
    }

    return "/ops/sys/notifications";
  }

  if (shell === "exe") {
    // 1. Specific entity detail views in EXE shell
    if (entities.lotCode) {
      return `/exe/ref/lots/${encodeURIComponent(entities.lotCode)}`;
    }
    if (entities.batchCode) {
      return `/exe/ref/losses/${encodeURIComponent(entities.batchCode)}`;
    }
    if (entities.ncrNo) {
      return `/exe/ref/ncrs/${encodeURIComponent(entities.ncrNo)}`;
    }

    // 2. Specific event views in EXE shell
    switch (n.eventType) {
      case "QcPending":
        return "/exe/ref/lots?filter=qc_pending";
      case "LotHeld":
        return "/exe/ref/lots?filter=hold";
      case "ExpiryWarning":
        return "/exe/ref/expiry";
      case "ProductionDelay":
        return "/exe/ref/losses";
      case "BatchCompleted":
        return "/exe/ref/losses";
      case "ApprovalPending":
        return n.entityId ? `/exe/approvals/${encodeURIComponent(n.entityId)}` : "/exe/approvals";
      default:
        break;
    }

    // 3. Entity type fallback
    if (n.entityType && n.entityId) {
      const type = n.entityType.toLowerCase();
      if (type === "lot") return `/exe/ref/lots/${encodeURIComponent(n.entityId)}`;
      if (type === "meshold" || type === "hold") return `/exe/ref/holds/${encodeURIComponent(n.entityId)}`;
      if (type === "ncr") return `/exe/ref/ncrs/${encodeURIComponent(n.entityId)}`;
      if (type === "batch" || type === "mesbatch" || type === "batchloss") return `/exe/ref/losses/${encodeURIComponent(n.entityId)}`;
      if (type === "approval") return `/exe/approvals/${encodeURIComponent(n.entityId)}`;
    }

    // 4. If explicit routeHint was given and it's already an EXE route
    if (n.routeHint?.startsWith("/exe/")) {
      return n.routeHint.trim();
    }

    // 5. Coarse or external routeHint normalized to EXE
    if (n.routeHint?.startsWith("/")) {
      const norm = normalizeNotificationRouteHint(n.routeHint, "exe", n);
      if (norm) return norm;
    }

    return "/exe/alerts";
  }

  // FIELD shell
  // 1. If explicit non-coarse routeHint provided for Field
  if (
    n.routeHint?.startsWith("/") &&
    n.routeHint !== "/field/today" &&
    n.routeHint !== "/field" &&
    n.routeHint !== "/exe/alerts"
  ) {
    return normalizeNotificationRouteHint(n.routeHint, "field", n);
  }

  // 2. Specific operation execution
  const opId = entities.operationId || (n.entityType === "Operation" ? n.entityId : null);
  if (
    (n.eventType === "OperationReady" || n.eventType === "OperationStarted") &&
    opId
  ) {
    return `/field/exec/${encodeURIComponent(opId)}`;
  }
  if (n.eventType === "QcPending" && opId) {
    return `/field/ipqc/${encodeURIComponent(opId)}`;
  }
  if (n.eventType === "CipRequired" || n.eventType === "CipOverdue") {
    return entities.batchCode
      ? `/field/cip?batch=${encodeURIComponent(entities.batchCode)}`
      : "/field/cip";
  }
  if (n.eventType === "ExpiryWarning") {
    return "/field/wms/expiry";
  }
  if (entities.lotCode) {
    return `/field/wms/stock?lot=${encodeURIComponent(entities.lotCode)}`;
  }
  if (n.eventType === "ApprovalPending") {
    return "/ops/sys/approvals";
  }

  // 3. Fallback to routeHint or event mapper
  if (n.routeHint?.startsWith("/")) {
    return normalizeNotificationRouteHint(n.routeHint, "field", n);
  }

  return routeFromNotificationEvent(n, "field");
}

/**
 * Rewrite legacy / cross-shell hints so Field/EXE stay on the right chrome.
 */
export function normalizeNotificationRouteHint(
  hint: string,
  shell: "field" | "exe",
  n: InAppNotification,
): string {
  let path = hint.trim();
  // Proto → prod channel prefixes
  if (path.startsWith("/mobile/") || path === "/mobile") {
    path = path.replace(/^\/mobile/, "/field");
  }
  if (path.startsWith("/executive/") || path === "/executive") {
    path = path.replace(/^\/executive/, "/exe");
  }
  if (path.startsWith("/app/") || path === "/app") {
    path = path.replace(/^\/app/, "/ops");
  }

  if (shell === "exe") {
    if (path.startsWith("/ops/sys/approvals")) return "/exe/approvals";
    if (path.startsWith("/field/") || path === "/field") return "/exe/alerts";
    if (path.startsWith("/exe/")) return path;
    // Ops risk surfaces → EXE alerts (no Ops escape)
    if (
      path.startsWith("/ops/mes/holds") ||
      path.startsWith("/ops/wms/") ||
      path.startsWith("/ops/qms/")
    ) {
      return "/exe/alerts";
    }
    return path.startsWith("/exe") ? path : "/exe/alerts";
  }

  // Field shell
  if (path.startsWith("/exe/") || path === "/exe") {
    // Field never enters EXE — map to local work surfaces
    if (path.includes("approvals")) return "/field/today";
    return "/field/today";
  }
  if (path.startsWith("/ops/sys/approvals")) {
    // Dual-shell personas (QD/QC) may open Ops approvals; pure Field keeps queue
    return "/ops/sys/approvals";
  }
  if (path.startsWith("/ops/wms/expiry")) return "/field/wms/expiry";
  if (path.startsWith("/ops/wms/lots") || path.startsWith("/ops/wms/stock")) {
    return "/field/wms/stock";
  }
  if (path.startsWith("/ops/mes/holds")) return "/field/today";
  if (path.startsWith("/ops/")) return "/field/today";

  // Prefer EXEC when BE left a coarse /field/today but payload has Operation id
  if (
    (path === "/field/today" || path.startsWith("/field/today?")) &&
    (n.eventType === "OperationReady" || n.eventType === "OperationStarted") &&
    n.entityType === "Operation" &&
    n.entityId
  ) {
    return `/field/exec/${encodeURIComponent(n.entityId)}`;
  }

  return path;
}

function routeFromNotificationEvent(
  n: InAppNotification,
  shell: "field" | "exe",
): string | null {
  const id = n.entityId?.trim() || "";
  switch (n.eventType) {
    case "OperationReady":
    case "OperationStarted":
      if (shell === "exe") return "/exe/alerts";
      return n.entityType === "Operation" && id
        ? `/field/exec/${encodeURIComponent(id)}`
        : "/field/today";
    case "QcPending":
      if (shell === "exe") return "/exe/alerts";
      return n.entityType === "Operation" && id
        ? `/field/ipqc/${encodeURIComponent(id)}`
        : "/field/today";
    case "CipRequired":
    case "CipOverdue":
      if (shell === "exe") return "/exe/alerts";
      if (
        (n.entityType === "Batch" || n.entityType === "MesBatch") &&
        id
      ) {
        return `/field/cip?batch=${encodeURIComponent(id)}`;
      }
      return "/field/cip";
    case "ApprovalPending":
      return shell === "exe" ? "/exe/approvals" : "/ops/sys/approvals";
    case "ExpiryWarning":
      return shell === "exe" ? "/exe/alerts" : "/field/wms/expiry";
    case "LotHeld":
      return shell === "exe" ? "/exe/alerts" : "/field/today";
    case "ProductionDelay":
    case "BatchCompleted":
      return shell === "exe" ? "/exe/alerts" : "/field/today";
    default:
      return null;
  }
}


export function notifyEventLabel(eventType: string): string {
  switch (eventType) {
    case "LotHeld":
      return "HOLD lot";
    case "BatchCompleted":
      return "Batch hoàn tất";
    case "ApprovalPending":
      return "Chờ duyệt";
    case "ExpiryWarning":
      return "Lot sắp hết hạn";
    case "OperationReady":
      return "Lệnh Ready";
    case "OperationStarted":
      return "Công đoạn Start";
    case "QcPending":
      return "QC chờ";
    case "CipRequired":
      return "CIP bắt buộc";
    case "CipOverdue":
      return "CIP quá hạn";
    case "ProductionDelay":
      return "SX trễ hạn";
    default:
      return eventType;
  }
}

export function notifyChannelLabel(channel: string): string {
  switch (channel) {
    case "inapp":
      return "Trong app";
    case "push":
      return "Push";
    case "email":
      return "Email";
    default:
      return channel;
  }
}

/** Display label for BE `RoleCodes` — human name (Ops + Field). */
export function roleCodeLabel(code: string): string {
  switch (code) {
    case "ROLE_ADMIN":
      return "Admin";
    case "ROLE_BGD":
      return "Ban giám đốc";
    case "ROLE_QA":
      return "QA";
    case "ROLE_QC":
      return "QC";
    case "ROLE_QD":
      return "Quản đốc";
    case "ROLE_NV_SX":
      return "Nhân viên SX";
    case "ROLE_KHO":
      return "Kho";
    case "ROLE_KE_HOACH":
      return "Kế hoạch";
    case "ROLE_KINH_DOANH":
      return "Kinh doanh";
    case "ROLE_MUA_HANG":
      return "Mua hàng";
    default:
      return code.replace(/^ROLE_/, "").replace(/_/g, " ");
  }
}
